/**
 * The conversation with a technician: a small panel at the foot of the
 * screen, out of the way of the person you are talking to.
 *
 * A projection of talkStore. Nothing here decides anything: the store owns
 * the transcript, the busy flag and the walk-lock; this renders them and
 * forwards the student's line. Escape reaches the store through main.tsx's
 * keydown, the same route the tool panel's Escape takes.
 */

import { useEffect, useRef, useState } from 'react'
import { t } from '../../i18n/i18n'
import { isTypingTarget } from '../../core/typing'
import { useLang } from '../i18n/LanguageProvider'
import { useTick } from '../state/useObservable'
import { talkStore } from '../../talk/talkStore'
import { talkWatch } from '../../talk/watch'
import { castName, castShortName } from '../../fab/cast'
import { canListen, canSpeak, listen, type ListenHandle } from '../../talk/voice'
import { TalkDemo } from './TalkDemo'
import { ReportView } from '../../ui/kit/ReportView'

/**
 * How far through the experiment the tool is, while it runs.
 *
 * The assistant's own progress, which counts CONDITIONS rather than
 * percent - "2 of 5, 300 W" - because that is what is actually happening:
 * the solver is running the case it just named. A spinner would hide the
 * one interesting fact about the wait.
 */
function RunProgress() {
  const p = talkStore.progress
  if (!p) return null
  return (
    <div className="talk-progress">
      {t('talk.experiment.Progress', { done: p.done, total: p.total })}
      {p.label ? ` · ${p.label}` : ''}
    </div>
  )
}

/**
 * Somebody catching your eye.
 *
 * The one thing on this floor that speaks first, and it is deliberately not
 * a sentence: a name and what it is about, and the technician says nothing
 * until the student takes it. That is what keeps a proactive floor from
 * being an interrupting one - it does not take the keyboard, does not stop
 * the walk, and costs nothing until it is wanted (watch.ts).
 *
 * Rendered where the conversation would be, so it appears in the place the
 * answer will appear, and is gone the moment either button is pressed.
 */
function Knock() {
  useTick(talkWatch.subscribe)
  const knock = talkWatch.pending
  if (!knock) return null
  return (
    <div className="talk-knock" role="status">
      <span className="talk-knock-text">
        <span className="talk-knock-who">{t('talk.knock.Wants', { name: knock.name })}</span>
        <span className="talk-knock-about">{knock.about}</span>
      </span>
      <button type="button" className="talk-knock-take" onClick={() => talkWatch.take()}>
        {t('talk.knock.Listen')}
      </button>
      <button type="button" className="talk-knock-go" onClick={() => talkWatch.dismiss()} aria-label={t('talk.knock.Dismiss')}>
        ×
      </button>
    </div>
  )
}

export function TalkPanel() {
  useLang()
  useTick(talkStore.subscribe)
  const who = talkStore.open
  const transcript = talkStore.transcript
  const [draft, setDraft] = useState('')
  /** What the microphone has heard so far, shown in the box until it firms up. */
  const [heard, setHeard] = useState('')
  const [hearing, setHearing] = useState(false)
  const [micNote, setMicNote] = useState('')
  const recognition = useRef<ListenHandle | null>(null)
  /**
   * Where in the question history the arrow keys have walked to, and the
   * half-typed line they walked away from. `null` means "not browsing":
   * the box is showing what the student typed, and Down does nothing.
   */
  const [historyAt, setHistoryAt] = useState<number | null>(null)
  /**
   * Which turn's scene is running, by index in the transcript.
   *
   * One at a time, and by default the newest: four Energy2D solvers and a
   * molecular-dynamics viewport scrolled off the top of the log would each
   * be taking their share of the frame with nobody watching them. An older
   * card offers to come back, and taking that offer moves the live one here.
   */
  const [liveDemo, setLiveDemo] = useState<number | null>(null)
  const stash = useRef('')
  const inputRef = useRef<HTMLInputElement>(null)
  const logRef = useRef<HTMLDivElement>(null)

  // The keyboard goes to the box the moment the person turns to you - and
  // comes back to it if a click on the floor took it away: a printable key
  // pressed with nothing focused focuses the box, early enough in the
  // event that the character lands in it.
  // A conversation that ends while the microphone is open takes it with it.
  useEffect(() => {
    if (who) return
    recognition.current?.abort()
    recognition.current = null
    setHearing(false)
    setHeard('')
    setMicNote('')
  }, [who])

  useEffect(() => {
    if (!who) return
    setDraft('')
    setHistoryAt(null)
    setLiveDemo(null)
    stash.current = ''
    inputRef.current?.focus()
    const reclaim = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return
      if (e.key.length === 1 || e.key === 'Backspace') inputRef.current?.focus()
    }
    window.addEventListener('keydown', reclaim, true)
    return () => window.removeEventListener('keydown', reclaim, true)
  }, [who])
  // The newest scene takes the floor from whatever was running, because it
  // is the one the technician just reached for. An older card the student
  // brought back keeps it only until the next one arrives.
  let newestDemo: number | null = null
  for (let i = 0; i < transcript.length; i++) if (transcript[i].demo) newestDemo = i
  /** A scene or a measured report - either one needs the card at its full width. */
  const tall = newestDemo !== null || transcript.some((turn) => turn.report || turn.running)

  useEffect(() => {
    const log = logRef.current
    if (!log) return
    // A line with a scene or a report under it is taller than the log, so
    // scrolling to the bottom the way every other turn does would land the
    // student in the middle of a canvas or a chart with the sentence that
    // introduced it already gone. Put the TOP of that turn at the top
    // instead: they read what she said, and the evidence is the next thing
    // down.
    const last = transcript[transcript.length - 1]
    const lead = last && (last.demo || last.report) ? log.children[transcript.length - 1] : null
    if (lead instanceof HTMLElement) log.scrollTop = lead.offsetTop - log.offsetTop
    else log.scrollTop = log.scrollHeight
  })
  useEffect(() => {
    if (newestDemo !== null) setLiveDemo(newestDemo)
  }, [newestDemo])

  // Nobody is being talked to - but somebody may want to be. See watch.ts.
  if (!who) return <Knock />
  const busy = talkStore.busy
  // She has asked them to call it and the tool is holding its report. The
  // box belongs to the student even though the turn is not over, so `busy`
  // is down and this is what says why. See src/talk/predict.ts.
  const awaiting = talkStore.awaiting

  const send = (line = draft): void => {
    const text = line.trim()
    // `talkStore.ask` routes a line typed while a prediction is pending to
    // the prediction rather than to the model, so nothing here has to know
    // which of the two this is.
    if (!text || (busy && !awaiting)) return
    setDraft('')
    setHeard('')
    setHistoryAt(null)
    stash.current = ''
    void talkStore.ask(text)
  }

  /**
   * Walk the question history, the way a terminal does: Up goes back
   * through what you have asked, Down comes forward again, and coming
   * forward past the newest gives you back the line you were part-way
   * through typing when you started looking.
   */
  const recall = (step: -1 | 1): void => {
    const lines = talkStore.history
    if (!lines.length) return
    let next: number | null
    if (historyAt === null) {
      if (step === 1) return // Not browsing: there is nothing ahead.
      stash.current = draft
      next = lines.length - 1
    } else {
      const moved = historyAt + step
      next = moved >= lines.length ? null : Math.max(0, moved)
    }
    setHistoryAt(next)
    const text = next === null ? stash.current : lines[next]
    setDraft(text)
    // The caret belongs at the end of a recalled line, not wherever it
    // happened to be in the line before it.
    requestAnimationFrame(() => {
      const el = inputRef.current
      if (el) el.setSelectionRange(el.value.length, el.value.length)
    })
  }

  /**
   * Dictate one question. The words appear in the box as they firm up, and
   * the finished sentence sends itself - a student who has just spoken a
   * question should not have to reach for the keyboard to send it.
   */
  const startDictation = (): void => {
    if (hearing || busy) return
    setHeard('')
    const handle = listen({
      interim: (text) => setHeard(text),
      final: (text) => {
        recognition.current = null
        setHearing(false)
        setHeard('')
        setMicNote('')
        send(`${draft}${draft && !draft.endsWith(' ') ? ' ' : ''}${text}`)
      },
      end: (reason) => {
        recognition.current = null
        setHearing(false)
        setHeard('')
        // The "where your audio goes" note belongs beside a live
        // microphone, so it goes when the microphone does. A refusal
        // stays up, since it is the reason nothing was heard.
        setMicNote(reason === 'denied' ? t('talk.MicDenied') : '')
      },
    })
    if (!handle) return
    recognition.current = handle
    setHearing(true)
    setMicNote(t('talk.MicWhere'))
  }

  const stopDictation = (): void => {
    recognition.current?.stop()
    recognition.current = null
    setHearing(false)
    setMicNote('')
  }

  return (
    // A scene needs a canvas and a graph beside it, and a report needs its
    // charts and its conditions table; 380px seats none of them. The card
    // widens once and stays wide for the rest of the conversation: flipping
    // back on the next line would resize every canvas in the log twice for
    // nothing.
    <div
      className={`talk${tall ? ' wide' : ''}`}
      role="dialog"
      aria-label={t('talk.Title', { name: castName(who.id) })}
    >
      <div className="talk-head">
        <div>
          <div className="tag">{t(`talk.cast.${who.id}.Role`)}</div>
          <div className="title">{castName(who.id)}</div>
        </div>
        {canSpeak() && (
          <button
            className={`talk-voice${talkStore.voice ? ' on' : ''}`}
            onClick={() => talkStore.setVoice(!talkStore.voice)}
            title={t(talkStore.voice ? 'talk.VoiceOff' : 'talk.VoiceOn')}
            aria-pressed={talkStore.voice}
          >
            {talkStore.voice ? '🔊' : '🔇'}
          </button>
        )}
        <button className="close" onClick={() => talkStore.close()} title={t('talk.Close')}>
          ×
        </button>
      </div>
      <div className="talk-log" ref={logRef}>
        {transcript.map((turn, i) => (
          <div key={i} className={`turn ${turn.role === 'student' ? 'student' : 'assistant'}`}>
            <div className="lead">{turn.text}</div>
            {turn.running && <RunProgress />}
            {turn.demo && (
              <TalkDemo
                id={turn.demo}
                live={i === liveDemo}
                onShow={() => setLiveDemo(i)}
                // The question this scene answers: the student's last line
                // before it. A scene with more than one face (the tunnelling
                // card's 2D box and 3D volume) opens on the one that was asked for.
                asked={transcript.slice(0, i).reverse().find((x) => x.role === 'student')?.text}
              />
            )}
            {/* The measured result, in the component the tool's own panel
                draws it with - it is the same report from the same
                assistant. Held to a reading column: a chart canvas follows
                its container, and one drawn the full width of a panel this
                wide would be half a metre tall. */}
            {turn.report && (
              <div className="talk-report">
                <ReportView lead="" report={turn.report} onAsk={(q) => send(q)} />
              </div>
            )}
            {turn.local && turn.role === 'technician' && i > 0 && <div className="talk-local">{t('talk.Local')}</div>}
            {turn.stopped && <div className="talk-local">{t('talk.Stopped')}</div>}
          </div>
        ))}
        {busy && <div className="turn assistant talk-busy">{t('talk.Thinking')}</div>}
      </div>
      {awaiting && (
        <div className="talk-predict">
          <span className="talk-predict-lead">{t('talk.predict.Waiting')}</span>
          <span className="talk-predict-ask">{awaiting}</span>
          <button type="button" className="talk-predict-skip" onClick={() => talkStore.skip()} title={t('talk.predict.SkipTitle')}>
            {t('talk.predict.Skip')}
          </button>
        </div>
      )}
      <form
        className="talk-ask"
        onSubmit={(e) => {
          e.preventDefault()
          send()
        }}
      >
        <input
          ref={inputRef}
          value={hearing ? `${draft}${heard}` : draft}
          maxLength={500}
          placeholder={
            hearing
              ? t('talk.Listening')
              : awaiting
                ? t('talk.predict.Placeholder')
                : t('talk.Placeholder', { name: castShortName(who.id) })
          }
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // A single-line box has no other use for the arrows, and the
            // walk is locked while a conversation is up, so they are free.
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault()
              recall(e.key === 'ArrowUp' ? -1 : 1)
            }
          }}
          disabled={(busy && !awaiting) || hearing}
        />
        {canListen() && (
          <button
            type="button"
            className={`talk-mic${hearing ? ' on' : ''}`}
            onClick={() => (hearing ? stopDictation() : startDictation())}
            title={t(hearing ? 'talk.MicStop' : 'talk.MicStart')}
            disabled={busy && !awaiting}
            aria-pressed={hearing}
          >
            🎙
          </button>
        )}
        {/* While the technician is answering, the button that sent the
            question is the one that calls it off - a stop control the
            student can find with the mouse, not only with Escape. */}
        {busy && !awaiting ? (
          <button type="button" className="talk-stop" onClick={() => talkStore.stop()} title={t('talk.Stop')}>
            {t('talk.Stop')}
          </button>
        ) : (
          <button type="submit" disabled={hearing || !draft.trim()}>
            {t('talk.Send')}
          </button>
        )}
      </form>
      {/* No key hints under the box - Escape and the up arrow are the
          shortcuts a text box already carries, and the line spent a strip
          of the panel saying so. Only the microphone note, which reports
          something the student cannot guess, keeps its place here. */}
      {micNote && (
        <div className="talk-key">
          <div className="talk-mic-note">{micNote}</div>
        </div>
      )}
    </div>
  )
}
